/*
 * Cabecera de Debbie Reda.
 *
 * Tres cosas, nada mas:
 *  0. Mide su propio alto y lo publica en --reda-header-h. La cabecera es
 *     `position: fixed`, asi que quien tiene que dejarle sitio necesita saber
 *     cuanto ocupa de verdad.
 *  1. Sticky que se retira al bajar y vuelve al subir. En cuanto hay scroll
 *     la cabecera pasa a solido, aunque el ajuste sea transparente.
 *  2. Panel a pantalla completa bajo 750px: abre, atrapa el foco, cierra con
 *     Escape y devuelve el foco al boton que lo abrio.
 *  3. Cierra el desplegable de idioma al pulsar fuera o con Escape. Esto es
 *     lo unico prescindible del archivo: el desplegable es un `details`, que
 *     abre y cierra solo. Sin JavaScript sigue funcionando, solo que hay que
 *     volver a pulsarlo para cerrarlo.
 */

if (!customElements.get('reda-cabecera')) {
  customElements.define(
    'reda-cabecera',
    class RedaCabecera extends HTMLElement {
      constructor() {
        super();

        this.barra = this.querySelector('.reda-header');
        this.boton = this.querySelector('[data-reda-abrir]');
        this.panel = this.querySelector('[data-reda-panel]');
        this.cerrar = this.querySelector('[data-reda-cerrar]');
        this.idiomas = this.querySelector('[data-reda-idiomas]');

        this.ultimoY = window.scrollY;
        this.pendiente = false;
        this.observadorAlto = null;
        this.altoPublicado = 0;

        this.alHacerScroll = this.alHacerScroll.bind(this);
        this.alPulsarTecla = this.alPulsarTecla.bind(this);
        this.abrirPanel = this.abrirPanel.bind(this);
        this.cerrarPanel = this.cerrarPanel.bind(this);
        this.alPulsarFuera = this.alPulsarFuera.bind(this);
        this.alEscaparIdiomas = this.alEscaparIdiomas.bind(this);
      }

      connectedCallback() {
        this.medirAlto();

        window.addEventListener('scroll', this.alHacerScroll, { passive: true });
        this.actualizarBarra();

        if (this.boton && this.panel) {
          this.boton.addEventListener('click', this.abrirPanel);
          this.panel.addEventListener('keydown', this.alPulsarTecla);
        }

        if (this.cerrar) this.cerrar.addEventListener('click', this.cerrarPanel);

        if (this.idiomas) {
          document.addEventListener('click', this.alPulsarFuera);
          this.idiomas.addEventListener('keydown', this.alEscaparIdiomas);
        }
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.alHacerScroll);
        document.removeEventListener('click', this.alPulsarFuera);
        if (this.observadorAlto) this.observadorAlto.disconnect();
        this.desbloquearPagina();
      }

      /* --- 0. Alto real --- */

      // El alto de la cabecera no es un numero que se pueda escribir a mano:
      // cambia entre movil y escritorio, y cambia si Debbie sube el ancho del
      // logo. Se mide y se publica en --reda-header-h, que es de donde lo leen
      // el hueco que reserva su sitio y los scroll-margin de las anclas.
      //
      // Ojo: la cabecera NO lee esa variable para su propio min-height, para
      // eso usa --reda-header-min. Si leyera la que ella misma publica, cada
      // medida alimentaria a la siguiente y el alto creceria sin parar.
      medirAlto() {
        if (!this.barra) return;

        this.publicarAlto();

        if (!('ResizeObserver' in window)) return;

        this.observadorAlto = new ResizeObserver(() => this.publicarAlto());
        this.observadorAlto.observe(this.barra);
      }

      publicarAlto() {
        const alto = Math.ceil(this.barra.getBoundingClientRect().height);
        if (!alto || alto === this.altoPublicado) return;

        this.altoPublicado = alto;
        document.documentElement.style.setProperty('--reda-header-h', alto + 'px');
      }

      /* --- 1. Sticky --- */

      alHacerScroll() {
        if (this.pendiente) return;
        this.pendiente = true;
        window.requestAnimationFrame(() => {
          this.actualizarBarra();
          this.pendiente = false;
        });
      }

      actualizarBarra() {
        if (!this.barra) return;

        const y = Math.max(window.scrollY, 0);
        const alto = this.barra.offsetHeight || 72;
        const tolerancia = 8;

        // Arriba del todo: la cabecera recupera su modo original.
        if (y <= 4) {
          this.barra.classList.remove('is-solida', 'is-oculta');
          this.ultimoY = y;
          return;
        }

        // A partir de aqui siempre solida.
        this.barra.classList.add('is-solida');

        // Con el panel abierto la cabecera no se mueve.
        if (this.panelAbierto()) {
          this.ultimoY = y;
          return;
        }

        if (y > this.ultimoY + tolerancia && y > alto) {
          this.barra.classList.add('is-oculta');
          this.ultimoY = y;
        } else if (y < this.ultimoY - tolerancia) {
          this.barra.classList.remove('is-oculta');
          this.ultimoY = y;
        }
      }

      /* --- 2. Panel --- */

      panelAbierto() {
        return this.panel != null && !this.panel.hasAttribute('hidden');
      }

      abrirPanel() {
        if (!this.panel) return;

        this.panel.removeAttribute('hidden');
        this.boton.setAttribute('aria-expanded', 'true');
        if (this.barra) this.barra.classList.remove('is-oculta');
        this.bloquearPagina();

        const primero = this.cerrar || this.enfocables()[0];
        if (primero) primero.focus();
      }

      cerrarPanel() {
        if (!this.panel || !this.panelAbierto()) return;

        this.panel.setAttribute('hidden', '');
        this.boton.setAttribute('aria-expanded', 'false');
        this.desbloquearPagina();
        this.boton.focus();
      }

      alPulsarTecla(evento) {
        if (evento.key === 'Escape') {
          evento.preventDefault();
          this.cerrarPanel();
          return;
        }

        if (evento.key !== 'Tab') return;

        // Foco atrapado dentro del panel mientras esta abierto.
        const lista = this.enfocables();
        if (lista.length === 0) return;

        const primero = lista[0];
        const ultimo = lista[lista.length - 1];

        if (evento.shiftKey && document.activeElement === primero) {
          evento.preventDefault();
          ultimo.focus();
        } else if (!evento.shiftKey && document.activeElement === ultimo) {
          evento.preventDefault();
          primero.focus();
        }
      }

      /* --- 3. Desplegable de idioma --- */

      alPulsarFuera(evento) {
        if (!this.idiomas.open) return;
        if (this.idiomas.contains(evento.target)) return;
        this.idiomas.open = false;
      }

      alEscaparIdiomas(evento) {
        if (evento.key !== 'Escape' || !this.idiomas.open) return;
        this.idiomas.open = false;
        // El foco vuelve al disparador, no se queda en el aire.
        const resumen = this.idiomas.querySelector('summary');
        if (resumen) resumen.focus();
      }

      enfocables() {
        if (!this.panel) return [];
        return Array.from(this.panel.querySelectorAll('a[href], button:not([disabled])')).filter(
          (elemento) => elemento.offsetParent !== null
        );
      }

      bloquearPagina() {
        document.body.style.overflow = 'hidden';
      }

      desbloquearPagina() {
        document.body.style.overflow = '';
      }
    }
  );
}
